<script lang="ts" setup>
import { useI18n } from "vue-i18n";
import { useDateTime } from "@/composables/useDateTime";
import { useFilters } from "@/composables/useFilters";
import { useAppStore } from "@/store/app.store";
import { TRUCK_RESERVATIONS_PAGE_SIZE } from "@/constants/truck";
import { removeTruckMention } from "@/utils/string";
import OmniTable from "@/components/omni/OmniTable.vue";
import type { LogRecordDto } from "@/api/service/dto/log-record.dto";

const props = withDefaults(
  defineProps<{
    items: LogRecordDto[];
    truckNumber: string;
    total?: number;
    page?: number;
    pageSize?: number;
    loading?: boolean;
  }>(),
  {
    total: 0,
    page: 1,
    pageSize: TRUCK_RESERVATIONS_PAGE_SIZE,
    loading: false,
  },
);

const emit = defineEmits<{
  (e: "update:page", page: number): void;
  (e: "update:pageSize", pageSize: number): void;
}>();

const { t } = useI18n();
const { dateTZ } = useDateTime();
const { mdash } = useFilters();
const appStore = useAppStore();

const headers = [
  {
    title: t("general.date-time"),
    key: "created",
    sortable: false,
  },
  {
    title: t("dispatchers.log-action"),
    key: "action",
    sortable: false,
  },
  {
    title: t("dispatchers.dispatcher"),
    key: "dispatcher",
    sortable: false,
  },
  {
    title: t("general.description"),
    key: "description",
    sortable: false,
  },
];

function formatCreatedDate(created: string) {
  return dateTZ(created, appStore.dispatcher?.timezone);
}

function formatDescription(description: string | null) {
  return mdash(removeTruckMention(description, props.truckNumber));
}

function updatePage(page: number) {
  emit("update:page", page);
}

function updatePageSize(pageSize: number) {
  emit("update:pageSize", pageSize);
}
</script>

<template>
  <omni-table
    :compact="true"
    :headers="headers"
    :items="props.items"
    :items-length="props.total"
    :items-per-page="props.pageSize"
    :loading="props.loading"
    :mobile-breakpoint="0"
    :page="props.page"
    :show-all-option="false"
    @update:page="updatePage"
    @update:items-per-page="updatePageSize"
  >
    <template #[`item.created`]="{ item }">
      <div class="text-no-wrap">{{ formatCreatedDate(item.created) }}</div>
    </template>
    <template #[`item.dispatcher`]="{ item }">
      <div>{{ item.dispatcher?.name }}</div>
    </template>
    <template #[`item.description`]="{ item }">
      <div class="description-cell">{{ formatDescription(item.description) }}</div>
    </template>
    <template #no-data>
      <div class="no-data-block">
        <span class="font-weight-500 text-grey-darken-3">{{ t("trucks.no-reservations-yet") }}</span>

        <span class="mt-1 text-grey-darken-2">{{ t("trucks.truck-has-not-been-reserved-yet") }}</span>
      </div>
    </template>
  </omni-table>
</template>

<style lang="scss" scoped>
.no-data-block {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 40px 0;
  width: 100%;
}

.description-cell {
  white-space: normal;
  word-break: break-word;
  padding: 8px 0;
}
</style>
